import React, { useEffect, useState } from 'react';
import { Clock, Coins, Save, Loader2, Presentation, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { calculate, DEFAULT_RATES, RATE_LABELS, money } from '../lib/pitchCalc';

const API = process.env.REACT_APP_BACKEND_URL;
const FIELDS = [
  ['members', 'Members', ''], ['hours_per_month', 'Committee hours per month on admin', 'hrs'],
  ['tool_spend', 'Annual software & tool spend', '$'], ['contractor_spend', 'Annual contractor / bookkeeper spend', '$'],
  ['renewals_per_year', 'Renewals per year', ''], ['meetings_per_year', 'Meetings per year', ''],
];
const DEFAULTS = { members: 60, hours_per_month: 40, tool_spend: 3000, contractor_spend: 0, renewals_per_year: 60, meetings_per_year: 11, running_cost: 500, hour_value: 48 };

function Bar({ label, before, after, fmt, testid }) {
  const max = Math.max(before, after, 1);
  return (
    <div data-testid={testid}>
      <div className="text-sm mb-1" style={{ color: 'var(--olive)' }}>{label}</div>
      {[['Before', before, '#9aa5bb'], ['After', after, '#1c6b2c']].map(([k, v, c]) => (
        <div key={k} className="flex items-center gap-2 mb-1">
          <span className="w-14 text-xs" style={{ color: 'var(--olive)' }}>{k}</span>
          <div className="flex-1 h-7 rounded" style={{ background: '#eee' }}><div className="h-7 rounded transition-all" style={{ width: `${Math.max(2, (v / max) * 100)}%`, background: c }} /></div>
          <span className="w-24 text-right font-title">{fmt(v)}</span>
        </div>
      ))}
    </div>
  );
}

// Public live savings calculator, linked from the pitch deck by QR code. Big type for a projector.
export default function PitchCalculator() {
  const { user } = useAuth();
  const [inp, setInp] = useState(DEFAULTS);
  const [rates, setRates] = useState(DEFAULT_RATES);
  const [showRates, setShowRates] = useState(false);
  const [defaults, setDefaults] = useState(null);
  const [orgName, setOrgName] = useState('');
  const [saving, setSaving] = useState(false);
  const [scenario, setScenario] = useState(null);

  useEffect(() => { fetch(`${API}/api/pitch/defaults`).then((r) => r.json()).then(setDefaults).catch(() => {}); }, []);

  const res = calculate(inp, rates);
  const set = (k) => (e) => setInp({ ...inp, [k]: e.target.value });
  const caseRes = defaults && defaults.case && defaults.case.hours_before ? {
    hours: [defaults.case.hours_before, defaults.case.hours_now], spend: [defaults.case.spend_before, defaults.case.spend_now],
  } : null;

  const save = async () => {
    if (!orgName.trim()) { toast.error('Enter your organisation\'s name first.'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/pitch/scenarios`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ org_name: orgName.trim(), inputs: inp, rates }) });
      if (!r.ok) throw new Error();
      const d = await r.json(); setScenario(d); toast.success(`Saved as a scenario for ${d.org_name}.`);
    } catch { toast.error('Could not save the scenario.'); } finally { setSaving(false); }
  };

  const generateDeck = async () => {
    let sc = scenario;
    if (!sc) { await save(); return; }
    const r = await fetch(`${API}/api/admin/pitch/deck.pptx?scenario_id=${sc.id}`, { credentials: 'include' });
    if (!r.ok) { toast.error('Could not build the deck.'); return; }
    const a = document.createElement('a'); a.href = URL.createObjectURL(await r.blob()); a.download = `pitch-${sc.org_name.replace(/\W+/g, '-')}.pptx`; a.click();
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="pitch-calculator">
      <h1 className="font-title text-3xl md:text-5xl mb-2" style={{ color: 'var(--maroon)' }}>Your numbers</h1>
      <p className="text-lg mb-6" style={{ color: 'var(--olive)' }}>Enter your organisation's figures — the estimate updates as you type. Saving-rate assumptions are adjustable below.</p>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-4">
          <input value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="Your organisation's name" data-testid="calc-org-name"
            className="w-full px-4 py-3 rounded border text-xl" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
          {FIELDS.map(([k, label, unit]) => (
            <label key={k} className="block">
              <span className="text-base font-title" style={{ color: 'var(--navy)' }}>{label}</span>
              <div className="flex items-center gap-2 mt-1">
                {unit === '$' && <span className="text-2xl font-title">$</span>}
                <input type="number" min="0" inputMode="numeric" value={inp[k]} onChange={set(k)} data-testid={`calc-${k}`}
                  className="w-full px-4 py-3 rounded border text-2xl font-title" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
                {unit === 'hrs' && <span className="text-lg" style={{ color: 'var(--olive)' }}>hrs</span>}
              </div>
            </label>
          ))}
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-sm" style={{ color: 'var(--olive)' }}>Platform running cost / year ($)</span>
              <input type="number" min="0" value={inp.running_cost} onChange={set('running_cost')} data-testid="calc-running_cost" className="w-full px-3 py-2 rounded border text-lg" style={{ borderColor: 'rgba(0,0,0,0.2)' }} /></label>
            <label className="block"><span className="text-sm" style={{ color: 'var(--olive)' }}>Value of a volunteer hour ($)</span>
              <input type="number" min="0" value={inp.hour_value} onChange={set('hour_value')} data-testid="calc-hour_value" className="w-full px-3 py-2 rounded border text-lg" style={{ borderColor: 'rgba(0,0,0,0.2)' }} /></label>
          </div>
          <button onClick={() => setShowRates(!showRates)} className="inline-flex items-center gap-1 text-sm underline" style={{ color: 'var(--navy)' }} data-testid="calc-toggle-rates"><SlidersHorizontal size={14} /> {showRates ? 'Hide' : 'Adjust'} saving-rate assumptions</button>
          {showRates && (
            <div className="space-y-2 p-3 rounded" style={{ background: 'var(--sage)' }} data-testid="calc-rates">
              {Object.keys(DEFAULT_RATES).map((k) => (
                <label key={k} className="flex items-center gap-3 text-sm">
                  <span className="w-56">{RATE_LABELS[k]}</span>
                  <input type="range" min="0" max="100" step="5" value={rates[k]} onChange={(e) => setRates({ ...rates, [k]: Number(e.target.value) })} data-testid={`calc-rate-${k}`} className="flex-1" />
                  <span className="w-12 text-right font-title">−{rates[k]}%</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg p-5 text-white" style={{ background: '#2b6cb0' }} data-testid="calc-hours-saved">
              <div className="flex items-center gap-2 text-sm opacity-90"><Clock size={16} /> Hours saved per year</div>
              <div className="font-title text-5xl mt-1">{res.hours_saved_year.toLocaleString()}</div>
              <div className="text-sm opacity-90 mt-1">≈ {res.hours_saved_month} hrs a month · worth {money(res.hours_value_year)}</div>
            </div>
            <div className="rounded-lg p-5 text-white" style={{ background: '#2f855a' }} data-testid="calc-money-saved">
              <div className="flex items-center gap-2 text-sm opacity-90"><Coins size={16} /> Dollars saved per year</div>
              <div className="font-title text-5xl mt-1">{money(res.money_saved_year)}</div>
              <div className="text-sm opacity-90 mt-1">after the platform's running cost</div>
            </div>
          </div>
          <div className="bg-white rounded-lg border p-5 space-y-5" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
            <Bar label="Committee hours per month" before={Number(inp.hours_per_month) || 0} after={res.hours_after_month} fmt={(v) => `${v} hrs`} testid="calc-bar-hours" />
            <Bar label="Annual spend" before={(Number(inp.tool_spend) || 0) + (Number(inp.contractor_spend) || 0)} after={res.spend_after_year} fmt={money} testid="calc-bar-spend" />
          </div>
          {caseRes && (
            <div className="rounded-lg p-5" style={{ background: 'var(--sage)' }} data-testid="calc-case-study">
              <div className="font-title text-lg mb-2" style={{ color: 'var(--maroon)' }}>Alongside: {defaults.org}</div>
              <div className="grid grid-cols-2 gap-3 text-base">
                <div><div className="text-xs" style={{ color: 'var(--olive)' }}>Hours per month</div><div className="font-title text-2xl">{caseRes.hours[0]} → {caseRes.hours[1]}</div></div>
                <div><div className="text-xs" style={{ color: 'var(--olive)' }}>Annual spend</div><div className="font-title text-2xl">{money(caseRes.spend[0])} → {money(caseRes.spend[1])}</div></div>
              </div>
              <p className="text-xs mt-2" style={{ color: 'var(--olive)' }}>Committee estimate{defaults.case.confirmed_date ? `, confirmed ${defaults.case.confirmed_date}` : ''}.</p>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <button onClick={save} disabled={saving} data-testid="calc-save" className="inline-flex items-center gap-2 px-5 py-3 rounded font-title text-lg" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} Save as scenario
            </button>
            {user && user.member_class === 'admin' && (
              <button onClick={generateDeck} data-testid="calc-generate-deck" className="inline-flex items-center gap-2 px-5 py-3 rounded font-title text-lg border" style={{ borderColor: 'var(--navy)', color: 'var(--navy)' }}>
                <Presentation size={18} /> {scenario ? 'Generate their deck' : 'Save, then generate their deck'}
              </button>
            )}
          </div>
          {scenario && <p className="text-sm" data-testid="calc-saved-note" style={{ color: 'var(--olive)' }}>Saved for <b>{scenario.org_name}</b>. The committee will follow up with a deck using these numbers.</p>}
          <p className="text-xs" style={{ color: 'var(--olive)' }}>Assumptions: hours split 25% renewals & receipting, 15% bank reconciliation, 20% minutes & agendas, 15% newsletter/list, 10% website, 15% other (no saving). Savings apply the rates above.</p>
        </div>
      </div>
    </div>
  );
}
